import { css } from '@emotion/react'
import { useEffect } from 'react'
import { useMedia } from 'react-use'

import MenuIcon from '@mui/icons-material/Menu'
import { ReactComponent as CivitasLogo } from '../../shared/images/CivitasLogo.svg'
import NavBarButton from '../../shared/layout/NavBarButton/NavBarButton'

import useAppSettings from '../../../lib/hooks/useAppSettings'
import useEnvironment from '../../../lib/hooks/useEnvironment'
import useKeyPress from '../../../lib/hooks/useKeyPress'
import useSchedulerLogin from '../../../lib/hooks/useSchedulerLogin'
import useScrollableKeyboardNavigation from '../../../lib/hooks/useScrollableKeyboardNavigation'
import useTheme from '../../../lib/hooks/useTheme'
import { userHasRole, userIsAdmin } from '../../../lib/authenticationUtils'
import { BP_SMALL_DOWN } from '../../../lib/styles/breakpoints'
import { oneDpShadowCss } from '../../../lib/styles/shadows'
import { dividerCss } from '../../../lib/styles/cssStyles'
import { getFontColor } from '../../../lib/colorUtils'
import { Auth0User, Roles } from '../../../types'
import {
  getAdminMainNav,
  getAdminOnlyNav,
  getAdvisingNav,
  getExternalAppLinks,
  getStudentMainNav,
  NavButton
} from './utils'

export type NavBarProps = {
  /** Whether skip link is within view */
  displaySkipLink?: boolean
  /** Whether nav menu is expanded over page content */
  isExpanded: boolean
  /** Whether the user is currently authenticated, from useAuth0 hook */
  isAuthenticated: boolean
  /** Determines whether nav menu is persisted alongside page content */
  isPersisted: boolean
  /** A function to expand nav menu over page content */
  onExpand: () => void
  /** A function to shrink the menu back to its default width */
  onShrink: () => void
  /** SkipLink focus callback. Not used by mobile navbar. */
  onSkipLinkFocus?: () => void
  /** SkipLink blur callback. Not used by mobile navbar. */
  onSkipLinkBlur?: () => void
  /** SkipLink click callback. Not used by mobile navbar. */
  onSkipLinkClick?: () => void
  /** A function to toggle whether the menu is persisted. Not used by mobile navbar. */
  toggleIsPersisted?: () => void
  /** User object from useAuth0 */
  user?: Auth0User | undefined
}

const NavBar = ({
  displaySkipLink = false,
  isAuthenticated,
  isExpanded,
  isPersisted,
  onExpand,
  onShrink,
  onSkipLinkBlur,
  onSkipLinkClick,
  onSkipLinkFocus,
  toggleIsPersisted,
  user
}: NavBarProps) => {
  const escapePress = useKeyPress('Escape')
  const isSmall = useMedia(`(${BP_SMALL_DOWN})`)
  const { navbarColor } = useTheme()
  const fontColor = getFontColor(navbarColor!)
  const isAdmin = isAuthenticated && userIsAdmin(user)
  const isStudent = isAuthenticated && userHasRole(user, [Roles.student])
  const environment = useEnvironment()
  const { academicPlanningSettings, text } = useAppSettings()
  const {
    advisingLabel,
    advisingUrl,
    enableAdvisingLink,
    adminAnalyticsLabel,
    adminAnalyticsUrl,
    enableAdminAnalyticsLink,
    degreeRequirementsPanelConfiguration
  } = academicPlanningSettings
  const courseLabel = text.course
  const scheduleLabel = text.schedule
  const { doSchedulerLogin } = useSchedulerLogin(undefined, undefined, 'navBar')
  const { scrollRef, handleKeyDown } = useScrollableKeyboardNavigation()

  useEffect(() => {
    if (isExpanded && escapePress) {
      onShrink()
      if (toggleIsPersisted && isPersisted) {
        toggleIsPersisted()
      }
    }
  }, [escapePress, isExpanded, isPersisted, onShrink, toggleIsPersisted])

  const toggleExpand = () => (isExpanded ? onShrink() : onExpand())
  const handleMenuClick = () => {
    if (isExpanded && !isPersisted) {
      toggleExpand()
      return
    }
    if (isSmall) {
      onShrink()
    } else {
      if (toggleIsPersisted) {
        toggleIsPersisted()
      }
      toggleExpand()
    }
  }
  const onMouseEnter = isPersisted ? undefined : onExpand
  const onMouseLeave = isPersisted ? undefined : onShrink

  const translate = '-235px'
  const transition = 'transition: all 0.3s ease;'

  const navCss = css`
    background-color: ${navbarColor};
    height: 100%;
    padding: 1rem;
    padding-top: 8px;
    position: fixed;
    ${transition}
    width: ${isExpanded ? '232px' : '52px'};
    z-index: 10100;
    ${isSmall && `transform: translate(${isExpanded ? '0' : translate});`}

    a {
      color: ${fontColor};
      text-decoration: none;
      text-align: center;
    }

    svg {
      fill: ${fontColor};
    }

    /* Copy MUI icon CSS for custom SVG */
    .customSvg {
      user-select: none;
      width: 1em;
      height: 1em;
      display: inline-block;
      fill: currentColor;
      flex-shrink: 0;
      transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      font-size: 1.5rem;
    }

    @media print {
      display: none;
    }
  `
  const listCss = css`
    list-style: none;
    padding: 0;
    margin: 0;

    a {
      width: ${isExpanded ? '198px' : '20px'};
    }
  `
  const skipLinkCss = css`
    align-items: center;
    background-color: #ffffff;
    display: flex;
    height: 2rem;
    position: fixed;
    transform: translateY(${displaySkipLink ? '0' : '-3rem'});
    transition: transform 0.3s ease;
    z-index: 10999;
  `
  const dividerContainerCss = css`
    ${dividerCss}
    margin-top: 10px;
    margin-bottom: 10px;
    border-bottom-color: ${fontColor}33; /* 33 is for 20% opacity */
  `
  const innerNavContainerCss = css`
    display: grid;
    grid-template-rows: 1fr 48px;
    overflow-y: ${isExpanded ? 'auto' : 'hidden'};
    overflow-x: hidden;
    height: 100%;
  `
  const logoContainerCss = css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    white-space: nowrap;

    path {
      fill: ${fontColor};
    }

    /* Only apply opacity to direct icon spans, not copyright text */
    svg + span {
      margin-left: 8px;
      opacity: 0.7;
    }
  `

  const copyrightTextCss = css`
    color: ${fontColor};
    font-size: 10px;
    line-height: 12px;
    letter-spacing: 0.65px;
    opacity: 1 !important; /* Override inherited opacity for WCAG compliance */
  `

  const menuButtonContainerCss = css`
    border-bottom: 1px solid ${fontColor}33; /* 33 is for 20% opacity */
  `

  const divider = <div css={dividerContainerCss} />

  const mainNav = isAdmin
    ? getAdminMainNav(user)
    : getStudentMainNav(
        courseLabel,
        scheduleLabel,
        doSchedulerLogin,
        degreeRequirementsPanelConfiguration
      )

  const adminOnlyNav = getAdminOnlyNav(user, environment)

  const renderNavLinks = (navLinks: Array<NavButton>) =>
    navLinks.map((b: NavButton) => (
      <li key={b.label}>
        {/* Keeping native <li> semantics for proper list structure (WCAG 1.3.1).
            Screen readers will announce "list with X items" and each item. */}
        <NavBarButton
          ariaLabel={b.ariaLabel}
          icon={b.icon}
          isExternalLink={b.isExternalLink}
          isNavExpanded={isExpanded}
          isNavPersisted={isPersisted}
          label={b.label}
          onClick={b.onClick}
          onShrink={onShrink}
          route={b.route}
          target={b.target}
          trackNavigation={b.trackNavigation}
          user={user}
        />
      </li>
    ))

  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <nav aria-label="Main" css={[navCss, oneDpShadowCss]}>
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */}
        <div
          id="main-nav-menu"
          css={innerNavContainerCss}
          ref={scrollRef}
          role="region"
          aria-label="Navigation menu"
          onKeyDown={handleKeyDown}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions */}
          {/* WCAG 2.1.2 Fix: Removed tabIndex to prevent focus trap.
               Navigation items inside are focusable, container should not be. */}
          <div>
            {/* WCAG 2.4.1: Skip link as anchor element for proper detection by accessibility tools */}
            <a
              href="#main-content"
              css={[skipLinkCss, oneDpShadowCss]}
              onBlur={onSkipLinkBlur}
              onFocus={onSkipLinkFocus}
              onClick={onSkipLinkClick}
            >
              Skip to Main Content
            </a>
            {isAuthenticated && (
              <ul css={listCss}>
                <li css={menuButtonContainerCss} key="expand-menu">
                  {/* WCAG 4.1.2: aria-expanded indicates whether the controlled content
                      (navigation labels) is expanded or collapsed. aria-controls points
                      to the element being controlled. */}
                  <NavBarButton
                    ariaControls="main-nav-menu"
                    ariaLabel={isExpanded || isPersisted ? 'Collapse Menu' : 'Expand Menu'}
                    expanded={isExpanded || isPersisted}
                    label="Menu"
                    icon={<MenuIcon />}
                    isNavExpanded={isExpanded}
                    isNavPersisted={isPersisted}
                    onClick={handleMenuClick}
                  />
                </li>
                {renderNavLinks(mainNav)}
              </ul>
            )}
            {isAuthenticated && isAdmin && (
              <div>
                {divider}
                <ul css={listCss}>
                  {renderNavLinks(
                    getAdvisingNav(
                      courseLabel,
                      scheduleLabel,
                      isStudent,
                      doSchedulerLogin,
                      degreeRequirementsPanelConfiguration
                    )
                  )}
                </ul>
              </div>
            )}
            {isAuthenticated && isAdmin && adminOnlyNav.length > 0 && (
              <div>
                {divider}
                <ul css={listCss}>{renderNavLinks(adminOnlyNav)}</ul>
              </div>
            )}
            {isAuthenticated &&
              (isAdmin ||
                (enableAdvisingLink && advisingUrl) ||
                (enableAdminAnalyticsLink && adminAnalyticsUrl)) && (
                <div>
                  {divider}
                  <ul css={listCss}>
                    {renderNavLinks(
                      getExternalAppLinks(
                        environment,
                        isAdmin,
                        enableAdvisingLink,
                        advisingUrl,
                        advisingLabel,
                        enableAdminAnalyticsLink,
                        adminAnalyticsUrl,
                        adminAnalyticsLabel
                      )
                    )}
                  </ul>
                </div>
              )}
          </div>

          <div css={logoContainerCss}>
            <CivitasLogo title="Civitas Learning Logo" aria-hidden="true" tabIndex={-1} />
            {isExpanded && <span css={copyrightTextCss}>Powered by Civitas Learning</span>}
          </div>
        </div>
      </nav>
    </div>
  )
}

export default NavBar
